import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, FileSpreadsheet, Loader2, Play, Upload } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import { maskUsDateInput, parseEffectiveDateInput } from './effectiveDateInput';
import FilterToolbar, { FilterForm } from './FilterToolbar';

const EMPTY_FILTERS = { person: '', q: '', cols: {} };

function compactCols(cols = {}) {
  const out = {};
  for (const [k, v] of Object.entries(cols)) {
    const t = String(v || '').trim();
    if (t) out[k] = t;
  }
  return out;
}

function filtersActive(f) {
  if (!f) return false;
  if (String(f.person || '').trim() || String(f.q || '').trim()) return true;
  return Object.values(f.cols || {}).some((v) => String(v || '').trim());
}

export default function EmployeeMasterReport({ onOracleReady, connectionRefresh }) {
  const [status, setStatus] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [running, setRunning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStatus = useCallback(() => {
    apiFetch('/api/reports/employee-master/status')
      .then(setStatus)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus, connectionRefresh]);

  useEffect(() => {
    if (status?.rowCount > 0) setRefreshKey((k) => k + 1);
  }, [status?.rowCount]);

  const dateParsed = parseEffectiveDateInput(effectiveDate);
  const liveAvailable = !!status?.liveRunAvailable && !!status?.connectionConfigured;

  const finish = (data, source) => {
    setResult(data);
    setRefreshKey((k) => k + 1);
    loadStatus();
    if (onOracleReady) onOracleReady({ source, ...data });
  };

  const uploadReport = async (file) => {
    if (!file) return;
    setError('');
    setResult(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${getApiBase()}/api/reports/employee-master/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      finish(data, 'employee-master-upload');
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runReport = async () => {
    setError('');
    setResult(null);
    // Effective date is optional; only validate when the user typed something.
    if (effectiveDate.trim() && !dateParsed.ok) {
      setError(dateParsed.error);
      return;
    }
    setRunning(true);
    try {
      const data = await apiFetch('/api/reports/employee-master/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          effectiveDate: effectiveDate.trim() ? dateParsed.iso : undefined,
          saveToDocs: true,
        }),
      });
      if (!data.ok) throw new Error(data.error || 'Report failed');
      finish(data, 'employee-master-report');
    } catch (e) {
      setError(e.message || 'Report failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-slate-300">
            As-of date <span className="text-slate-500">(optional)</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="MM/DD/YYYY"
            className="input-field w-36 font-mono"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(maskUsDateInput(e.target.value))}
          />
        </label>
        <button
          type="button"
          onClick={runReport}
          disabled={running || uploading || !liveAvailable}
          className="btn-primary"
          title={
            liveAvailable
              ? 'Pull the Employee Master from Oracle'
              : 'Live pull is not available on this connection. Upload the Excel export, or ask your consultant to install the Employee Master report.'
          }
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {running ? 'Running (this can take several minutes)…' : 'Run report'}
        </button>
        <label className="btn-secondary inline-flex cursor-pointer">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload Excel
          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            disabled={uploading || running}
            onChange={(e) => {
              const f = e.target.files?.[0];
              uploadReport(f);
              e.target.value = '';
            }}
          />
        </label>
        {status?.connection?.name && (
          <span className="text-xs text-slate-500">{status.connection.name}</span>
        )}
      </div>

      {!liveAvailable && status && (
        <p className="mb-3 text-xs text-slate-500">
          Live &ldquo;Run report&rdquo; is inactive until the Employee Master report is installed
          on this Oracle tenant. For now, <strong className="text-slate-300">Upload Excel</strong> to
          load the extract. Once saved, it feeds Demographic Recon and the Pre-Payroll Employment /
          Banking / Email checks automatically.
        </p>
      )}

      {error && <p className="alert-error mb-3">{error}</p>}

      {result?.ok && (
        <div className="alert-success mb-3 text-sm">
          {result.rowCount?.toLocaleString()} rows · {result.columnCount} columns
          {result.savedPath ? ' · saved' : ''}
        </div>
      )}

      <EmployeeMasterTable refreshKey={refreshKey} />
    </div>
  );
}

function EmployeeMasterTable({ refreshKey = 0 }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const hasActiveFilters = filtersActive(applied);

  const loadRows = useCallback(async (active) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (active.person) params.set('person', active.person);
      if (active.q) params.set('q', active.q);
      const cols = compactCols(active.cols);
      if (Object.keys(cols).length) params.set('cols', JSON.stringify(cols));
      params.set('limit', '500');
      const payload = await apiFetch(`/api/reports/employee-master/rows?${params.toString()}`);
      setData(payload);
    } catch (e) {
      setError(e.message || 'Failed to load rows');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows(applied);
  }, [loadRows, applied, refreshKey]);

  const applyFilters = () =>
    setApplied({
      person: filters.person,
      q: filters.q,
      cols: compactCols(filters.cols),
    });

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const setColFilter = (col, value) =>
    setFilters((f) => ({ ...f, cols: { ...f.cols, [col]: value } }));

  const downloadExcel = async () => {
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams();
      if (applied.person) params.set('person', applied.person);
      if (applied.q) params.set('q', applied.q);
      const cols = compactCols(applied.cols);
      if (Object.keys(cols).length) params.set('cols', JSON.stringify(cols));
      const res = await fetch(
        `${getApiBase()}/api/reports/employee-master/export?${params.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Export failed. Try again.');
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="([^"]+)"/i.exec(cd);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || 'employee-master.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!data && !loading && !error) return null;
  if (data && data.total === 0 && !hasActiveFilters && !loading) return null;

  const columns = data?.columns || [];
  const summaryText =
    data?.total != null
      ? `${data.total.toLocaleString()} person row(s)${hasActiveFilters ? ' matching filters' : ''}${
          data.columnCount ? ` · ${data.columnCount} columns` : ''
        }${data.total > (data.rows?.length || 0) ? ` · showing first ${data.rows.length}` : ''}`
      : 'Loading…';

  return (
    <div className="mt-4">
      <div className="card-panel overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            aria-expanded={expanded}
          >
            <span className="text-sm font-semibold text-slate-200">Employee master results</span>
            <span className="truncate text-xs text-slate-500">{summaryText}</span>
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn-secondary shrink-0 !p-1.5"
            title={expanded ? 'Minimize results' : 'Expand results'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {expanded && (
          <FilterForm onApply={applyFilters} disabled={loading} className="space-y-3 border-t border-slate-700/80 px-3 pb-3 pt-3">
            <FilterToolbar
              onClear={clearFilters}
              hasActiveFilters={hasActiveFilters}
              disabled={loading}
              extra={
                <button
                  type="button"
                  onClick={downloadExcel}
                  disabled={exporting || !data?.total}
                  className="btn-secondary py-1.5 text-sm"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4" />
                  )}
                  Excel
                </button>
              }
            >
              <input
                value={filters.person}
                onChange={(e) => setFilters((f) => ({ ...f, person: e.target.value }))}
                placeholder="Person number…"
                className="filter-input text-sm"
                title="Matches Person Number (and Person Id if you type that instead)"
              />
              <input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Search all columns…"
                className="filter-input min-w-[12rem] text-sm"
              />
            </FilterToolbar>

            {exportError && <p className="text-xs text-rose-400">{exportError}</p>}
            {error && <p className="alert-error">{error}</p>}

            <div className="overflow-hidden rounded-lg border border-slate-700/80">
              <div className="max-h-[min(480px,55vh)] overflow-auto">
                <table className="data-table w-full text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      {columns.map((col) => (
                        <th key={col} className="whitespace-nowrap px-2 py-2 text-left font-medium">
                          {col}
                        </th>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      {columns.map((col) => (
                        <th key={`${col}-filter`} className="px-2 py-1.5">
                          <input
                            value={filters.cols?.[col] || ''}
                            onChange={(e) => setColFilter(col, e.target.value)}
                            placeholder="Filter…"
                            className="filter-input min-w-[5.5rem]"
                            aria-label={`Filter ${col}`}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !data?.rows?.length ? (
                      <tr>
                        <td colSpan={columns.length || 1} className="px-3 py-8 text-center text-slate-500">
                          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                        </td>
                      </tr>
                    ) : data?.rows?.length ? (
                      data.rows.map((row, idx) => (
                        <tr key={idx}>
                          {columns.map((col) => (
                            <td key={col} className="whitespace-nowrap px-2 py-1.5 text-slate-200">
                              {row[col] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={columns.length || 1} className="px-3 py-6 text-center text-slate-500">
                          No rows match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </FilterForm>
        )}
      </div>
    </div>
  );
}
